import { ReactNode, createContext, useContext } from 'react';
import { CountryCode, LanguageCode } from 'typescript/constants/localization';

interface LocaleContextType {
  countryCode: CountryCode;
  languageCode: LanguageCode;
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

/*
  Custom hook to extract locale details

  Usage:
    const localeInfo = useLocaleInfo();
    countryCode = localeInfo.countryCode;
    languageCode = localeInfo.languageCode;
 */
const useLocaleInfo = () => {
  const context = useContext(LocaleContext);
  if (context === undefined) {
    throw new Error('useLocaleInfo must be used within a LocaleInfoProvider');
  }
  return context;
};

interface LocaleInfoProviderProps {
  children: ReactNode;
  countryCode: CountryCode;
  languageCode: LanguageCode;
}

/*
  Provider to set the locale details

  Usage:
    <LocaleInfoProvider countryCode={CountryCode.US} languageCode={LanguageCode.EN}>
      <App />
    </LocaleInfoProvider>
 */
const LocaleInfoProvider = ({
  children,
  countryCode,
  languageCode,
}: LocaleInfoProviderProps) => {
  const values = {
    countryCode,
    languageCode,
  };

  return (
    <LocaleContext.Provider value={values}>{children}</LocaleContext.Provider>
  );
};

export { LocaleInfoProvider, useLocaleInfo };
